import React, { useEffect, useMemo, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import Slider from 'react-slick';
import PreviousArrow from './PreviousArrow';
import NextArrow from './NextArrow';

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'a',
  'button',
  'input',
  'select',
  'textarea',
  '[role="button"]',
  '[role="link"]',
  '[tabindex]',
].join(',');

const getListEl = (ref) => ref?.current?.innerSlider?.list || null;

const isActuallyDisabled = (el) => {
  if (!el) return true;
  return el.hasAttribute && el.hasAttribute('disabled');
};

const getSlideIndexFromTarget = (target) => {
  const slide = target?.closest?.('.slick-slide');
  if (!slide) return null;
  if (slide.classList.contains('slick-cloned')) return null;

  const raw = slide.getAttribute('data-index');
  const idx = raw != null ? parseInt(raw, 10) : NaN;
  return Number.isFinite(idx) ? idx : null;
};

// ✅ Desktop: only slick-current tabbable
// ✅ 200%/Mobile: visible slides tabbable
const applyRovingTabIndex = (listEl, tabAdvancesSlides) => {
  if (!listEl) return;

  listEl.querySelectorAll('.slick-slide').forEach((slide) => {
    const isCloned = slide.classList.contains('slick-cloned');
    if (isCloned) return;

    const isCurrent = slide.classList.contains('slick-current');

    // Fractional slidesToShow: use slick-active OR aria-hidden=false (more reliable)
    const isVisible =
      slide.classList.contains('slick-active') ||
      slide.getAttribute('aria-hidden') === 'false';

    const isTabbableSlide = tabAdvancesSlides ? isVisible : isCurrent;

    slide.querySelectorAll(FOCUSABLE_SELECTOR).forEach((el) => {
      if (isActuallyDisabled(el)) return;
      
      // Skip elements with aria-hidden="true" - they should remain untabbable
      if (el.getAttribute('aria-hidden') === 'true') {
        el.tabIndex = -1;
        return;
      }
      
      el.tabIndex = isTabbableSlide ? 0 : -1;
    });
  });
};

const focusFirstInCurrentSlide = (listEl) => {
  const slide = listEl?.querySelector('.slick-slide.slick-current:not(.slick-cloned)');
  if (!slide) return false;

  const els = Array.from(slide.querySelectorAll(FOCUSABLE_SELECTOR))
    .filter((el) => !isActuallyDisabled(el))
    .filter((el) => el.getAttribute('aria-hidden') !== 'true');

  if (els[0] && typeof els[0].focus === 'function') {
    els[0].focus();
    return true;
  }
  return false;
};

const focusLastInCurrentSlide = (listEl) => {
  const slide = listEl?.querySelector('.slick-slide.slick-current:not(.slick-cloned)');
  if (!slide) return false;

  const els = Array.from(slide.querySelectorAll(FOCUSABLE_SELECTOR))
    .filter((el) => !isActuallyDisabled(el))
    .filter((el) => el.getAttribute('aria-hidden') !== 'true');

  const last = els[els.length - 1];
  if (last && typeof last.focus === 'function') {
    last.focus();
    return true;
  }
  return false;
};

const Carousel = (props) => {
  const {
    children,
    slidesToShow,
    currentTab,
    prevArrow,
    nextArrow,
    afterChange,
    tabAdvancesSlides,
    isHomepageCarousel,
    ...rest
  } = props;

  const sliderRef = useRef(null);
  const pendingFromNextRef = useRef(false);

  // Prevent loops when slickGoTo triggers focus updates
  const movingToFocusedSlideRef = useRef(false);

  const slidesToBeShown = useMemo(() => {
    if (!children) return 1;
    return slidesToShow > children.length ? children.length : slidesToShow;
  }, [children, slidesToShow]);

  const syncApplyRoving = useCallback(() => {
    const listEl = getListEl(sliderRef);
    applyRovingTabIndex(listEl, tabAdvancesSlides);
  }, [tabAdvancesSlides]);

  useEffect(() => {
    syncApplyRoving();
    const listEl = getListEl(sliderRef);
    requestAnimationFrame(() => applyRovingTabIndex(listEl, tabAdvancesSlides));
  }, [currentTab, children, slidesToBeShown, syncApplyRoving, tabAdvancesSlides]);

  const afterChangeWrapped = (index) => {
    afterChange?.(index);

    syncApplyRoving();
    const listEl = getListEl(sliderRef);
    requestAnimationFrame(() => applyRovingTabIndex(listEl, tabAdvancesSlides));
  };

  // ✅ Mobile/200%: when any slide receives focus, move carousel to show it
  // ✅ BUT clamp target so we never create end whitespace on fractional slidesToShow
  useEffect(() => {
    if (!tabAdvancesSlides) return;

    const listEl = getListEl(sliderRef);
    if (!listEl) return;

    const onFocusIn = (e) => {
      if (movingToFocusedSlideRef.current) return;

      const idx = getSlideIndexFromTarget(e.target);
      if (idx == null) return;

      const currentIdx = sliderRef?.current?.innerSlider?.state?.currentSlide;
      if (currentIdx === idx) return;

      movingToFocusedSlideRef.current = true;

      // Use actual current slidesToShow from slick (accounts for responsive),
      // fallback to computed slidesToBeShown.
      const currentSlidesToShow =
        sliderRef?.current?.innerSlider?.props?.slidesToShow ?? slidesToBeShown;

      // ✅ Prevent end whitespace:
      // last valid "start index" = total - ceil(slidesToShow)
      const totalItems = children?.length ?? 0;
      const maxStart = Math.max(0, totalItems - Math.ceil(currentSlidesToShow));

      // Clamp so we don't scroll beyond maxStart
      const targetIndex = Math.min(idx, maxStart);

      sliderRef.current?.slickGoTo?.(targetIndex);

      requestAnimationFrame(() => {
        const updatedListEl = getListEl(sliderRef);
        applyRovingTabIndex(updatedListEl, tabAdvancesSlides);
        movingToFocusedSlideRef.current = false;
      });
    };

    listEl.addEventListener('focusin', onFocusIn);

    return () => {
      listEl.removeEventListener('focusin', onFocusIn);
    };
  }, [tabAdvancesSlides, children, slidesToBeShown]);

  // Keep your existing behavior (apply roving before entering)
  const handleFocusCapture = () => {
    syncApplyRoving();
  };

  const wrapBackwardFromPrev = () => {
    const listEl = getListEl(sliderRef);
    return focusLastInCurrentSlide(listEl);
  };

  return (
    <Slider
      ref={sliderRef}
      {...rest}
      afterChange={afterChangeWrapped}
      slidesToShow={slidesToBeShown}
      onFocusCapture={handleFocusCapture}
      prevArrow={
        <PreviousArrow
          currentTab={currentTab}
          wrapBackwardFromPrev={wrapBackwardFromPrev}
          isHomepageCarousel={isHomepageCarousel}
        >
          {prevArrow}
        </PreviousArrow>
      }
      nextArrow={
        <NextArrow
          currentTab={currentTab}
          totalItems={children.length}
          markPendingFocusToSlide={() => {
            pendingFromNextRef.current = true;
          }}
          shouldMoveFocusOnNextTab={() => pendingFromNextRef.current}
          clearPendingFocusToSlide={() => {
            pendingFromNextRef.current = false;
          }}
          moveFocusToCurrentSlide={() => {
            const listEl = getListEl(sliderRef);
            applyRovingTabIndex(listEl, tabAdvancesSlides);
            return focusFirstInCurrentSlide(listEl);
          }}
          isHomepageCarousel={isHomepageCarousel}
        >
          {nextArrow}
        </NextArrow>
      }
    >
      {children}
    </Slider>
  );
};

Carousel.propTypes = {
  children: PropTypes.any,
  slidesToShow: PropTypes.number,
  currentTab: PropTypes.number,
  prevArrow: PropTypes.node,
  nextArrow: PropTypes.node,
  afterChange: PropTypes.func,

  // ✅ New: enabled under 896 (mobile + 200% zoom typical)
  tabAdvancesSlides: PropTypes.bool,
  // ✅ Homepage carousel keyboard trap fix
  isHomepageCarousel: PropTypes.bool,
};

Carousel.defaultProps = {
  tabAdvancesSlides: false,
  isHomepageCarousel: false,
};

export default Carousel;